import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return (
      detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
        .filter(Boolean)
        .join(" ") || "Please check the form."
    );
  return String(detail);
};

const Auth = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const url = mode === "signin" ? "/api/auth/login-email" : "/api/auth/register";
      const body =
        mode === "signin"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, password: form.password };
      const res = await fetch(`${API}${url}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(fmtError(data.detail));
      window.dispatchEvent(new Event("nox-auth-changed"));
      navigate("/account");
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6 pb-24 pt-28">
      <div className="starfield pointer-events-none absolute inset-x-0 top-0 h-[400px] opacity-50" aria-hidden="true" />
      <div className="relative w-full max-w-md border border-line bg-surface p-8" data-testid="auth-card">
        <MoonMark className="mx-auto h-10 w-10 text-gold" />
        <h1 className="mt-6 text-center font-display text-2xl tracking-[0.2em] text-silver">
          {mode === "signin" ? "RETURN TO THE NIGHT" : "JOIN THE NIGHT"}
        </h1>
        <p className="mt-2 text-center text-xs text-slate-500">
          {mode === "signin"
            ? "Sign in to track your quotes and replies."
            : "Create an account to follow your commission from first note to fulfilment."}
        </p>

        <div className="mt-8 grid grid-cols-2 border border-line" data-testid="auth-tabs">
          <button
            type="button"
            data-testid="auth-tab-signin"
            onClick={() => setMode("signin")}
            className={`cursor-pointer px-4 py-2.5 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
              mode === "signin" ? "bg-[#17171f] text-gold" : "text-slate-500 hover:text-silver"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            data-testid="auth-tab-register"
            onClick={() => setMode("register")}
            className={`cursor-pointer px-4 py-2.5 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
              mode === "register" ? "bg-[#17171f] text-gold" : "text-slate-500 hover:text-silver"
            }`}
          >
            Create Account
          </button>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          {mode === "register" && (
            <input
              className="input-night"
              placeholder="Your name"
              value={form.name}
              onChange={set("name")}
              data-testid="auth-name"
            />
          )}
          <input
            className="input-night"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={set("email")}
            data-testid="auth-email"
          />
          <input
            className="input-night"
            type="password"
            placeholder={mode === "signin" ? "Password" : "Password (at least 8 characters)"}
            value={form.password}
            onChange={set("password")}
            data-testid="auth-password"
          />
          {error && (
            <p className="text-xs text-red-500" data-testid="auth-error">
              {error}
            </p>
          )}
          <button className="btn-night w-full" type="submit" disabled={busy} data-testid="auth-submit">
            {busy ? "One moment…" : mode === "signin" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-600">
          Quotes are tracked by email — messages sent before joining appear in your{" "}
          <Link to="/account" data-testid="auth-link-account" className="text-gold/80 hover:text-gold">
            My Quotes
          </Link>{" "}
          after you sign up with the same address.
        </p>
      </div>
    </main>
  );
};

export default Auth;
